import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axiosInstance from '../../api/axiosInstance';
import { useSocket } from "../../context/SocketContext";
import { Capacitor } from '@capacitor/core';
import { Filesystem, Directory } from '@capacitor/filesystem';
import getCameraPlugin from '../../utils/cameraPlugin';
import '../../styles/seeContent.css';
import { CircularProgress } from '@mui/material';

const SeeContentChat = () => {
  const { socket } = useSocket();

  const navigate = useNavigate();
  const location = useLocation();
  const { photo, video, chatId, userId, replyTo } = location.state || {};

  const CameraPlugin = getCameraPlugin();

  const videoRef = useRef(null);
  const [isAudioOn, setIsAudioOn] = useState(true);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    const forcePlay = async () => {
      if (videoRef.current && video) {
        try {
          videoRef.current.pause();
          videoRef.current.load();
  
          await new Promise((resolve) => setTimeout(resolve, 100));
  
          await videoRef.current.play();
          console.log("✅ Video playback started");
        } catch (err) {
          console.warn("⚠️ Video play failed:", err);
        }
      }
    };
  
    forcePlay();
  }, [video]);

  const handleSend = async () => {
    setSending(true);
    try {
      const isVideo = !!video;
      const fileBlob = await fetch(isVideo ? video : photo).then((res) => res.blob());

      const res = await axiosInstance.post('/chat/signed-url', {
        type: fileBlob.type,
        messageType: isVideo ? 'video' : 'photo',
      });

      const { uploadUrl, messageId, filename } = res.data;

      await fetch(uploadUrl, {
        method: 'PUT',
        headers: { 'Content-Type': fileBlob.type },
        body: fileBlob,
      });

      socket.emit('chat message', {
        id: messageId,
        chat_id: chatId,
        sender: userId,
        content: filename,
        type: isVideo ? 'video' : 'photo',
        is_reply_to: replyTo?.message_id || null,
      });

      navigate(`/chat/${chatId}`);
    } catch (err) {
      console.error('Error while sending content to chat:', err);
      alert('An error occurred while sending the content.');
    } finally {
      setSending(false);
    }
  };

  const handleDiscard = async () => {
    if (videoRef.current) {
      videoRef.current.pause();
      videoRef.current.removeAttribute('src');
      videoRef.current.load();
    }

    if (video?.startsWith('blob:')) {
      URL.revokeObjectURL(video);
    }

    if (Capacitor.getPlatform() === 'android' || Capacitor.getPlatform() === 'ios') {
      navigate(-1);

      const result = await CameraPlugin.openCamera({ animated: false });
      
      if (!result || !result.fileUri) {
        console.error('Error: No fileUri received from android or iOS.');
        return;
      }

      if (result.isVideo) {
        navigate('/see-content-chat', { state: { video: result.fileUri, chatId, userId, replyTo } });
      }
      else {
        navigate('/see-content-chat', { state: { photo: result.fileUri, chatId, userId, replyTo } });
      }
    }
  };

  const handleDownload = async () => {
    if (Capacitor.getPlatform() === 'android' || Capacitor.getPlatform() === 'ios') {
      try {
        const isVideo = !!video;
        let filePath = isVideo ? video : photo;

        if (filePath.startsWith("data:video/mp4;base64,")) {
          console.log("📂 Converting Base64 video to file...");
    
          const base64Data = filePath.split(",")[1];
    
          const savedFile = await Filesystem.writeFile({
            path: "saved_video.mp4",
            data: base64Data,
            directory: Directory.Documents,
            recursive: true,
          });
    
          filePath = savedFile.uri;
    
          console.log("✅ Base64 video saved to:", filePath);
        }
    
        console.log("📂 Final File Path Sent to Native:", filePath);
    
        await CameraPlugin.saveToGallery({
          filePath,
          isVideo,
          stripAudio: !isAudioOn,
        });
      } catch (error) {
        console.error(error);
      }
    }
  };

  const toggleAudio = () => {
    setIsAudioOn(prev => !prev);
  };

  return (
    <div className="tailwind see-content-container">
      {(photo || video) ? (
        <>
          <div className="top-right-icons">
            <div className="icon-wrapper" onClick={handleDiscard}>
              <img
                src='assets/icons/discard.png'
                alt="Discard"
                className="icon-button"
              />
            </div>
            <div className="icon-wrapper" onClick={handleDownload}>
              <img
                src='assets/icons/download.png'
                alt="Download"
                className="icon-button"
              />
            </div>
            {video && (
              <div className="icon-wrapper" onClick={toggleAudio}>
                <img
                  src={isAudioOn ? 'assets/icons/audio_on.png' : 'assets/icons/audio_off.png'}
                  alt="Audio"
                  className="icon-button"
                />
              </div>
            )}
          </div>
          {photo ? (
            <img src={photo} alt="Captured" className="captured-photo" />
          ) : (
            <video
              ref={videoRef}
              loop
              playsInline
              autoPlay
              muted={!isAudioOn}
              className="captured-video"
              style={{ marginBottom: "20px", width: "100%" }}
            >
              <source src={video} type="video/mp4" />
              Your browser does not support the video tag.
            </video>
          )}
          <div className="flex justify-center mt-4">
            <button
              onClick={handleSend}
              disabled={sending}
              className="px-6 py-2 rounded-full font-semibold text-white bg-chatBlueColor"
            >
              {sending ? <CircularProgress size={20} sx={{ color: 'white' }} /> : 'Send'}
            </button>
          </div>
        </>
      ) : (
        <>
          <button className="close-button" onClick={handleDiscard}>
            <img src="assets/icons/close.png" alt="Close" />
          </button>
          <p style={{ color: "white", fontSize: "large" }}>No photo or video to display</p>
        </>
      )}
    </div>
  );
};

export default SeeContentChat;